import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import { Backdrop, CircularProgress } from '@mui/material'
import ProtectedRoute from './components/feature/protected-routes/ProtectedRoute'
import Header from './components/layout/header/Header'
import DisclaimerPopup from './components/layout/disclaimer-popup/DisclaimerPopup'
import SilentRenewCallback from './components/feature/silent-renew-callbak/SilentRenewCallback'
import { useAuth } from './context/auth-context/AuthContext'
import useMenuBar from './hooks/UseMenuBar'
import { PathName } from './lib/CommonLib'
import { useTrackPageView } from './hooks/useTrackPageView'
import 'mapbox-gl/dist/mapbox-gl.css'
import './App.css'

// Lazy-loaded components
const LandingPage = lazy(() => import('./pages/landing-page/LandingPage'))
const LoginPage = lazy(() => import('./pages/login-page/LoginPage'))
const UploadPage = lazy(() => import('./pages/upload-page/UploadPage'))
const MapViewPage = lazy(() => import('./pages/map-view-page/MapViewPage'))
const TimeSeriesPage = lazy(() => import('./pages/time-series-page/TimeSeriesPage'))
const ContactUsPage = lazy(() => import('./pages/contact-us-page/ContactUsPage'))
const DocumentPage = lazy(() => import('./pages/document-page/DocumentPage'))
const EventPage = lazy(() => import('./pages/event-page/EventPage'))
const DataPointPage = lazy(() => import('./pages/data-point-page/DataPointPage'))

const protectedComponents = {
  [PathName.Map]: MapViewPage,
  [PathName.Timeseries]: TimeSeriesPage,
  [PathName.Upload]: UploadPage,
  [PathName.Document]: DocumentPage,
  [PathName.Event]: EventPage,
  [PathName.DataPoint]: DataPointPage,
  [PathName.ContactUs]: ContactUsPage,
}

function App() {
  const { baseUrl, user, loading: authLoading } = useAuth()
  const { fetchMenuBar } = useMenuBar()
  const isEnable = process.env.REACT_APP_ENABLE_MATOMO === 'true'

  useTrackPageView({
    enabled: isEnable,
    trackInitialPage: true,
  })

  useEffect(() => {
    if (baseUrl && user?.token) {
      fetchMenuBar()
    }
  }, [baseUrl, user?.token])

  return (
    <div className="App">
      <Header />
      <Suspense
        fallback={
          <Backdrop
            sx={{
              color: '#fff',
              zIndex: 100,
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
            open={authLoading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        }
      >
        <Routes>
          <Route path={PathName.LandingPage} element={<LandingPage />} />
          <Route path={PathName.Login} element={<LoginPage />} />
          <Route path={PathName.SilentRenewToken} element={<SilentRenewCallback />} />

          {Object.entries(protectedComponents).map(([path, Component]) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute>
                  <Component />
                </ProtectedRoute>
              }
            />
          ))}
        </Routes>
      </Suspense>
      <DisclaimerPopup />
    </div>
  )
}

export default App
